import { toFormatPrice } from "../common/helpers/function";

/**
 * Dashboard KPI overview — single payload shape for cards + future API mapping.
 * Replace or pass a partial from `PosDashboard` after `fetch`; UI merges with {@link mergeKpiDashboardData}.
 *
 * @typedef {Object} KpiPaymentSegment
 * @property {string} color CSS color (hex) for donut slice + legend dot
 * @property {number} percent Share 0–100 (used for conic-gradient)
 * @property {string} [label] Override legend name (e.g. API locale). Else i18n fallback by row index.
 *
 * @typedef {Object} KpiTotalSale
 * @property {number} mainAmount Primary headline total (same currency as POS settings)
 * @property {number} grossAmount
 * @property {number} discountAmount Negative for discount
 * @property {number} vsPreviousPercent vs prior period (absolute display e.g. 8.5 → "8.50%")
 * @property {boolean} vsPreviousUp
 *
 * @typedef {Object} KpiPayment
 * @property {number} [donutTotalAmount] If set, center label uses {@link toFormatPrice}
 * @property {string} [donutTotalDisplay] Pre-formatted center label if amounts come as strings from API
 * @property {KpiPaymentSegment[]} segments
 * @property {number} [refundAmount] Cash refund total from header `payment_method.refund_amount` (shown below donut legend)
 *
 * @typedef {Object} KpiCustomer
 * @property {number} totalPeople Headline count
 * @property {number} vsPreviousPercent
 * @property {boolean} vsPreviousUp
 * @property {number} returnCount
 * @property {number} newCount
 * @property {number} avgSpentPerPerson
 *
 * @typedef {Object} KpiProduct
 * @property {number} totalPcs Headline
 * @property {number} vsPreviousPercent
 * @property {boolean} vsPreviousUp
 * @property {number} posCount
 * @property {number} customOrderCount
 * @property {number} exchangeRefundCount
 *
 * @typedef {Object} KpiDashboardData
 * @property {KpiTotalSale} totalSale
 * @property {KpiPayment} payment
 * @property {KpiCustomer} customer
 * @property {KpiProduct} product
 */

/** @type {KpiDashboardData} */
export const DEFAULT_KPI_DASHBOARD_DATA = {
  totalSale: {
    mainAmount: 1000000,
    grossAmount: 1111111,
    discountAmount: -111111,
    vsPreviousPercent: 8.5,
    vsPreviousUp: true,
  },
  payment: {
    donutTotalDisplay: "1,000,000.00",
    donutTotalAmount: null,
    refundAmount: 0,
    segments: [
      { color: "#5C74C7", percent: 10, amount: 100000 },
      { color: "#5CC7C1", percent: 50, amount: 500000 },
      { color: "#F9BC44", percent: 40, amount: 400000 },
      { color: "#4E8EF0", percent: 0, amount: 0 },
    ],
  },
  customer: {
    totalPeople: 100,
    vsPreviousPercent: 10,
    vsPreviousUp: false,
    returnCount: 40,
    newCount: 60,
    avgSpentPerPerson: 25000,
  },
  product: {
    totalPcs: 200,
    vsPreviousPercent: 15,
    vsPreviousUp: true,
    posCount: 150,
    customOrderCount: 30,
    exchangeRefundCount: 20,
  },
};

/**
 * @param {KpiPaymentSegment[]} segments
 * @returns {string} `conic-gradient(...)` for the donut ring
 */
export function buildPaymentDonutGradient(segments) {
  const active = (segments || []).filter((s) => s && s.percent > 0);
  if (active.length === 0) {
    return "conic-gradient(#e8e8e8 0deg 360deg)";
  }
  let deg = 0;
  const stops = active.map((s) => {
    const start = deg;
    deg += (s.percent / 100) * 360;
    return `${s.color} ${start}deg ${deg}deg`;
  });
  return `conic-gradient(${stops.join(", ")})`;
}

/**
 * @param {number|string} value
 * @returns {string} e.g. "8.50%"
 */
export function formatTrendPercent(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "0.00%";
  return `${Math.abs(n).toFixed(2)}%`;
}

/**
 * Resolve payment donut center text.
 * @param {KpiPayment} payment
 * @returns {string}
 */
export function resolveDonutCenterLabel(payment) {
  if (payment.donutTotalAmount != null && payment.donutTotalAmount !== "") {
    return toFormatPrice(Number(payment.donutTotalAmount));
  }
  return payment.donutTotalDisplay ?? "";
}

/**
 * Legend line for a payment segment.
 * @param {KpiPaymentSegment} seg
 * @returns {string}
 */
// export function formatPaymentSegmentMeta(seg) {
//   if (seg.metaDisplay) return seg.metaDisplay;
//   const pct = Number(seg.percent);
//   const pctLabel = `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
//   if (seg.amount == null) return `0 (${pctLabel})`;
//   return `${toFormatPrice(Number(seg.amount))} (${pctLabel})`;
// }
export function formatPaymentSegmentMeta(seg) {
  if (seg.metaDisplay) return { main: seg.metaDisplay, pctLabel: null };
  const pct = Number(seg.percent);
  const pctLabel = `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  const main = seg.amount == null ? "0" : toFormatPrice(Number(seg.amount));
  return { main, pctLabel };
}
/**
 * Deep-merge API/partial payload with defaults so callers can send only changed branches.
 * @param {Partial<KpiDashboardData> | null | undefined} partial
 * @returns {KpiDashboardData}
 */
export function mergeKpiDashboardData(partial) {
  const base = DEFAULT_KPI_DASHBOARD_DATA;
  if (!partial || typeof partial !== "object") {
    return {
      totalSale: { ...base.totalSale },
      payment: { ...base.payment, segments: base.payment.segments.map((s) => ({ ...s })) },
      customer: { ...base.customer },
      product: { ...base.product },
    };
  }

  return {
    totalSale: { ...base.totalSale, ...partial.totalSale },
    payment: {
      ...base.payment,
      ...partial.payment,
      segments:
        partial.payment && partial.payment.segments !== undefined
          ? partial.payment.segments
          : base.payment.segments.map((s) => ({ ...s })),
    },
    customer: { ...base.customer, ...partial.customer },
    product: { ...base.product, ...partial.product },
  };
}

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeLabel = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const byLabel = (rows, label) => {
  const wanted = normalizeLabel(label);
  return (Array.isArray(rows) ? rows : []).find((r) => normalizeLabel(r?.label) === wanted) ?? null;
};

/** Looks up modal headline total from table + summary (API may use different row labels per section). */
const pickModalMainCurrent = (node, candidates) => {
  const merged = [
    ...(Array.isArray(node?.table) ? node.table : []),
    ...(Array.isArray(node?.summary) ? node.summary : []),
  ];
  for (const cand of candidates) {
    const row = byLabel(merged, cand);
    if (row != null) return toNum(row?.current, 0);
  }
  return 0;
};

const mapPoints = (points) =>
  (Array.isArray(points) ? points : []).map((p) => ({
    x: String(p?.x ?? ""),
    current: toNum(p?.current, 0),
    previous: toNum(p?.previous, 0),
  }));

/**
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<KpiDashboardData>}
 */
export function mapKpiHeaderApiToDashboardData(apiResponse) {
  const header = apiResponse?.header ?? {};
  const totalSale = header.total_sale ?? {};
  const payment = header.payment_method ?? {};
  const customer = header.customer ?? {};
  const product = header.product ?? {};

  const saleRows = totalSale.summary ?? [];
  const customerRows = customer.summary ?? [];
  const productRows = product.summary ?? [];
  const paymentRows = payment.chart?.data ?? [];

  const saleTrendPercent = toNum(totalSale.vs_previous_period?.percent, 0);
  const customerTrendPercent = toNum(customer.vs_previous_period?.percent, 0);
  const productTrendPercent = toNum(product.vs_previous_period?.percent, 0);

  const saleTotal = byLabel(saleRows, "Total Sale");
  const saleGross = byLabel(saleRows, "Gross Sale");
  const saleDiscount = byLabel(saleRows, "Discount");

  const customerTotal = byLabel(customer.table ?? [], "Total");
  const customerReturn = byLabel(customerRows, "Return Customer");
  const customerNew = byLabel(customerRows, "New Customer");
  const customerAvg = byLabel(customerRows, "AVG Spent");

  const productTotal = byLabel(product.table ?? [], "Total");
  const productPos = byLabel(productRows, "POS");
  const productCustom = byLabel(productRows, "Custom Order");
  const productExchangeRefund = byLabel(productRows, "Exchange / Refund");

  return {
    totalSale: {
      mainAmount: toNum(saleTotal?.current, 0),
      grossAmount: toNum(saleGross?.current, 0),
      discountAmount: toNum(saleDiscount?.current, 0),
      vsPreviousPercent: Math.abs(saleTrendPercent),
      vsPreviousUp: String(totalSale.vs_previous_period?.direction ?? "").toLowerCase() !== "down",
    },
    payment: {
      donutTotalAmount: paymentRows.reduce((sum, item) => sum + toNum(item?.amount, 0), 0),
      refundAmount: toNum(payment?.refund_amount, 0),
      segments: paymentRows.map((item, idx) => ({
        color: DEFAULT_KPI_DASHBOARD_DATA.payment.segments[idx]?.color ?? "#5CC7C1",
        label: String(item?.label ?? ""),
        amount: toNum(item?.amount, 0),
        percent: toNum(item?.percent, 0),
      })),
    },
    customer: {
      totalPeople: toNum(customerTotal?.current, 0),
      vsPreviousPercent: Math.abs(customerTrendPercent),
      vsPreviousUp: String(customer.vs_previous_period?.direction ?? "").toLowerCase() !== "down",
      returnCount: toNum(customerReturn?.current, 0),
      newCount: toNum(customerNew?.current, 0),
      avgSpentPerPerson: toNum(customerAvg?.current, 0),
    },
    product: {
      totalPcs: toNum(productTotal?.current, 0),
      vsPreviousPercent: Math.abs(productTrendPercent),
      vsPreviousUp: String(product.vs_previous_period?.direction ?? "").toLowerCase() !== "down",
      posCount: toNum(productPos?.current, 0),
      customOrderCount: toNum(productCustom?.current, 0),
      exchangeRefundCount: toNum(productExchangeRefund?.current, 0),
    },
  };
}

/**
 * @param {object | null | undefined} apiResponse
 */
export function mapKpiHeaderApiToModalData(apiResponse) {
  const header = apiResponse?.header ?? {};
  const makeModal = (node, fallbackLabel, mainCandidates = ["Total"]) => ({
    mainCurrent: pickModalMainCurrent(node, mainCandidates),
    trendPercent: Math.abs(toNum(node?.vs_previous_period?.percent, 0)),
    trendUp: String(node?.vs_previous_period?.direction ?? "").toLowerCase() !== "down",
    diff: toNum(node?.vs_previous_period?.diff, 0),
    currentPeriod: String(node?.chart?.current_period ?? ""),
    previousPeriod: String(node?.chart?.previous_period ?? ""),
    points: mapPoints(node?.chart?.points),
    table: (Array.isArray(node?.table) ? node.table : node?.summary ?? []).map((r) => ({
      label: String(r?.label ?? fallbackLabel),
      current: toNum(r?.current, 0),
      previous: toNum(r?.previous, 0),
    })),
  });

  return {
    totalSale: makeModal(header.total_sale ?? {}, "Total Sale", ["Total Sale", "Total"]),
    customer: makeModal(header.customer ?? {}, "Customer", ["Total"]),
    product: makeModal(header.product ?? {}, "Product", ["Total"]),
  };
}
